import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, AlertCircle, Loader2, ArrowRight } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "checking", payment_status: null });
  const attempts = useRef(0);

  /* eslint-disable */
  useEffect(() => {
    if (!sessionId) { setState({ phase: "error" }); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/billing/checkout/${sessionId}`);
        // "paid" for immediate charge, "no_payment_required" for trial signups (card
        // on file but not charged yet). Both mean the subscription is provisioned.
        const paid = r.data.payment_status === "paid" || r.data.payment_status === "no_payment_required";
        if (paid || r.data.applied) {
          // Safety net: force a reconcile so subscribed_until is definitely synced
          // even if the webhook is misconfigured in this environment.
          try { await api.post("/billing/reconcile"); } catch { /* best effort */ }
          setState({ phase: "success", billing: r.data.billing });
          return;
        }
        if (r.data.status === "expired") {
          setState({ phase: "expired" });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setState({ phase: "timeout" });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setState({ phase: "error" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);
  /* eslint-enable */

  return (
    <div className="min-h-screen bg-[color:var(--bg)] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {state.phase === "checking" && (
          <div data-testid="billing-checking">
            <Loader2 className="h-10 w-10 text-[color:var(--accent)] mx-auto animate-spin" strokeWidth={1.5} />
            <h2 className="font-heading text-2xl mt-6 text-[color:var(--fg)]">Confirming your payment…</h2>
            <p className="text-sm text-[color:var(--fg-3)] mt-2 font-mono-num">SESSION {sessionId?.slice(0, 20)}…</p>
          </div>
        )}
        {state.phase === "success" && (
          <div data-testid="billing-success">
            <CheckCircle2 className="h-12 w-12 text-[color:var(--accent)] mx-auto" strokeWidth={1.5} />
            <div className="label-eyebrow mt-4 text-[color:var(--accent)]">SUBSCRIPTION ACTIVE</div>
            <h2 className="font-heading text-3xl mt-2 text-[color:var(--fg)]">Welcome aboard, operator.</h2>
            <p className="text-sm text-[color:var(--fg-2)] mt-3">
              You&apos;re now subscribed. Your edge starts compounding today.
            </p>
            <button onClick={() => navigate("/")} data-testid="goto-dashboard-success"
              className="mt-8 bg-[color:var(--accent)] text-black px-5 py-3 text-sm hover:bg-[color:var(--accent)] transition-colors flex items-center gap-2 mx-auto">
              GO TO DASHBOARD <ArrowRight className="h-4 w-4" strokeWidth={1.5}/>
            </button>
          </div>
        )}
        {(state.phase === "error" || state.phase === "expired" || state.phase === "timeout") && (
          <div data-testid="billing-error">
            <AlertCircle className="h-12 w-12 text-rose-400 mx-auto" strokeWidth={1.5} />
            <h2 className="font-heading text-2xl mt-4 text-[color:var(--fg)]">
              {state.phase === "expired" ? "Session expired" : state.phase === "timeout" ? "Still processing…" : "Something went wrong"}
            </h2>
            <p className="text-sm text-[color:var(--fg-3)] mt-3">
              {state.phase === "timeout"
                ? "We didn't get confirmation in time. Check your email or refresh in a moment."
                : "Please try again."}
            </p>
            <button onClick={() => navigate("/pricing")} className="mt-6 text-xs text-[color:var(--accent)] hover:underline">
              BACK TO PRICING
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
